/**
 * Account Trash API — the archived org/project/board tier of the unified Trash.
 *
 * Backend lives in apps/chaos-gateway/src/trash-archived.ts. All routes 404
 * with `{ error: 'trash_disabled' }` when the gateway TRASH_ENABLED flag is OFF
 * (default), so callers should degrade gracefully on a 404.
 *
 *   GET    /api/account/trash                                 (account-owner)
 *   POST   /api/orgs/:orgId/restore                           (founder)
 *   POST   /api/orgs/:orgId/projects/:projectId/restore       → projects-v2.ts
 *   POST   /api/boards/:boardId/restore                       → boards.ts
 *   DELETE /api/orgs/:orgId/projects/:projectId/purge         → projects-v2.ts
 *   DELETE /api/boards/:boardId/purge                         → boards.ts
 *
 * Org PERMANENT delete is intentionally not here — that is the guided
 * Danger-Zone teardown (orgTeardownApi). Org RESTORE (un-archive) is here.
 */
import { apiJson } from './client';

export interface ArchivedOrg {
  id: string;
  slug: string;
  name: string;
  archivedAt: string | null;
}
export interface ArchivedProject {
  id: string;
  slug: string;
  name: string;
  orgId: string;
  orgName: string | null;
  archivedAt: string | null;
}
export interface ArchivedBoard {
  id: string;
  slug: string;
  name: string;
  orgId: string;
  orgName: string | null;
  projectId: string;
  projectName: string | null;
  archivedAt: string | null;
}

/**
 * An archived Kanban column. Added 2026-07-26 with the column soft-delete: the
 * board's "… → Delete" used to hard-`DELETE FROM chaos.columns`, so there was
 * nothing to list. `boardId` is the id the restore route wants — the gateway
 * COALESCEs it to the project id on legacy project-keyed columns, so it is
 * always present and always restorable.
 */
export interface ArchivedColumn {
  id: string;
  name: string;
  boardId: string;
  boardName: string | null;
  orgId: string;
  orgName: string | null;
  archivedAt: string | null;
}

/**
 * An archived DIVISION / DEPARTMENT / org ROLE. Added 2026-07-26: all three
 * carried `archived_at` from the start and nothing in the gateway ever cleared
 * it — no listing, no restore, no purge. Restore heals any parent pointer that
 * no longer resolves and reports what it healed; it is refused with 409
 * `slug_conflict` when a live sibling has taken the name in the meantime.
 */
export interface ArchivedOrgUnit {
  id: string;
  slug: string;
  name: string;
  orgId: string;
  orgName: string | null;
  archivedAt: string | null;
}

/**
 * An archived GLOBAL role. It belongs to no account, so the gateway returns it
 * to a SYSTEM FOUNDER only and gives everyone else `[]` — the same gate every
 * other /api/global-roles route uses. An archived global role also 404s on its
 * own GET, which is why Trash was the only place it could ever have surfaced.
 */
export interface ArchivedGlobalRole {
  id: string;
  slug: string;
  name: string;
  archivedAt: string | null;
}

export interface AccountTrashResponse {
  orgs: ArchivedOrg[];
  projects: ArchivedProject[];
  boards: ArchivedBoard[];
  columns: ArchivedColumn[];
  divisions: ArchivedOrgUnit[];
  departments: ArchivedOrgUnit[];
  roles: ArchivedOrgUnit[];
  globalRoles: ArchivedGlobalRole[];
}

/** What restoring an org-structure row answers. `healed` names every parent
 *  pointer that no longer resolved and was cleared — e.g. `['department']`. */
export interface OrgUnitRestoreResponse {
  healed: string[];
  sessionsToRebuild?: number;
}

/**
 * Account-wide trashed CARDS + KNOWLEDGE FILES (the `deleted_at` tier),
 * spanning every org in the owner's account — the account-wide companion to
 * the per-scope GET /api/trash. Restore / permanent-delete reuse the
 * existing id-based mutations in `trashApi` (lib/api/trash.ts).
 */
export interface TrashedCardItem {
  id: string;
  title: string;
  orgId: string | null;
  orgName: string | null;
  deletedAt: string;
}
export interface TrashedFileItem {
  id: string;
  scope: 'org' | 'board' | 'role' | 'card';
  scopeId: string;
  filename: string;
  sizeBytes: number;
  mimeType: string | null;
  description: string | null;
  orgName: string | null;
  deletedAt: string;
}
/**
 * A trashed VOICE RECORDING (migration 0156). Until 2026-07-26 the Voice tab's
 * Delete unlinked the audio and dropped the row in the same breath — 46 lifetime
 * deletes of bytes that exist nowhere else. `sizeBytes` is what the upload
 * claimed; the REAL size on disk, which is what a permanent delete frees, is in
 * Settings -> Storage -> Trash, which lstats the file it is about to unlink.
 */
export interface TrashedVoiceInputItem {
  id: string;
  cardId: string;
  cardTitle: string | null;
  orgId: string | null;
  orgName: string | null;
  filename: string;
  sizeBytes: number;
  durationSec: number | null;
  transcript: string | null;
  deletedAt: string;
}

/**
 * A trashed NOTE (migration 0156). Its replies still point at it, so restoring
 * it restores the thread — the hard delete this replaced NULLed them via
 * `ON DELETE SET NULL` and the thread could not be rebuilt.
 */
export interface TrashedNoteItem {
  id: string;
  cardId: string;
  cardTitle: string | null;
  orgId: string | null;
  orgName: string | null;
  authorDisplayName: string;
  body: string;
  isReply: boolean;
  deletedAt: string;
}

export interface AccountTrashItemsResponse {
  cards: TrashedCardItem[];
  files: TrashedFileItem[];
  /** Optional: an older gateway mid-rolling-deploy omits these keys entirely.
   *  Every consumer must use `?? []` — see the note on `columns` above. */
  voiceInputs?: TrashedVoiceInputItem[];
  notes?: TrashedNoteItem[];
}

export const accountTrashApi = {
  list(): Promise<AccountTrashResponse> {
    return apiJson<AccountTrashResponse>('/account/trash');
  },
  /** Account-wide trashed cards + knowledge files (account-owner scoped). */
  listItems(): Promise<AccountTrashItemsResponse> {
    return apiJson<AccountTrashItemsResponse>('/account/trash/items');
  },
  restoreOrg(orgId: string): Promise<{ ok: true; id: string }> {
    return apiJson<{ ok: true; id: string }>(`/orgs/${orgId}/restore`, { method: 'POST' });
  },
  /**
   * Un-archive a column. It comes back EMPTY — its cards were re-homed to the
   * board's leftmost column when it was archived and are not dragged back.
   */
  restoreColumn(boardId: string, columnId: string): Promise<{ column: { id: string } }> {
    return apiJson<{ column: { id: string } }>(`/boards/${boardId}/columns/${columnId}/restore`, {
      method: 'POST',
    });
  },

  // ── Org structure: divisions / departments / roles / global roles ──
  // Restore comes back WITHOUT the children that were relocated on archive, and
  // NULLs any parent pointer that no longer resolves (reported in `healed`).
  // Purge is founder-only, refuses anything still live, and answers 409
  // `referenced` naming the tables that block it rather than a raw 23503.
  restoreDivision(orgId: string, id: string): Promise<OrgUnitRestoreResponse> {
    return apiJson<OrgUnitRestoreResponse>(`/orgs/${orgId}/divisions/${id}/restore`, {
      method: 'POST',
    });
  },
  restoreDepartment(orgId: string, id: string): Promise<OrgUnitRestoreResponse> {
    return apiJson<OrgUnitRestoreResponse>(`/orgs/${orgId}/departments/${id}/restore`, {
      method: 'POST',
    });
  },
  restoreRole(orgId: string, id: string): Promise<OrgUnitRestoreResponse> {
    return apiJson<OrgUnitRestoreResponse>(`/orgs/${orgId}/roles/${id}/restore`, {
      method: 'POST',
    });
  },
  restoreGlobalRole(id: string): Promise<OrgUnitRestoreResponse> {
    return apiJson<OrgUnitRestoreResponse>(`/global-roles/${id}/restore`, { method: 'POST' });
  },
  purgeDivision(orgId: string, id: string): Promise<{ ok: true }> {
    return apiJson<{ ok: true }>(`/orgs/${orgId}/divisions/${id}/purge`, { method: 'DELETE' });
  },
  purgeDepartment(orgId: string, id: string): Promise<{ ok: true }> {
    return apiJson<{ ok: true }>(`/orgs/${orgId}/departments/${id}/purge`, { method: 'DELETE' });
  },
  purgeRole(orgId: string, id: string): Promise<{ ok: true }> {
    return apiJson<{ ok: true }>(`/orgs/${orgId}/roles/${id}/purge`, { method: 'DELETE' });
  },
  purgeGlobalRole(id: string): Promise<{ ok: true }> {
    return apiJson<{ ok: true }>(`/global-roles/${id}/purge`, { method: 'DELETE' });
  },
};

export const accountTrashKeys = {
  all: ['account-trash'] as const,
  list: () => [...accountTrashKeys.all, 'list'] as const,
  items: () => [...accountTrashKeys.all, 'items'] as const,
};
